'use client';

import { useEffect, useRef } from 'react';
import { Button, Chips, Heading } from '@petsmart-ui/sparky';
import {
    useClearRefinements,
    useCurrentRefinements,
} from 'react-instantsearch';

import '@app/plp/components/ClearFilters/ClearFilters.scss';
import type { ClearFiltersProps } from '@app/plp/components/ClearFilters/types';
import {
    FILTERS,
    getRangeTitle,
    transformFilterItems,
    transformFilters,
    useFeaturedBrand,
} from '@libs/algolia';
import type { ItemFilterType } from '@app/plp/utils/types';
import { usePriceRangeContext } from '@app/plp/providers/PriceRangeProvider';
import { CATEGORIES_ATTRIBUTE } from '@app/plp/components/SearchLayout/utils';

export default function ClearFilters({ hasHeader }: ClearFiltersProps) {
    const isFeatured = useFeaturedBrand();
    const excludedAttributes = [CATEGORIES_ATTRIBUTE, 'query'];
    if (isFeatured) {
        excludedAttributes.push(FILTERS.BRAND.ATTRIBUTE);
    }
    const { items } = useCurrentRefinements({
        excludedAttributes,
    });
    const { refine: clearRefine } = useClearRefinements({
        excludedAttributes,
    });
    const actualFilters = useRef<string[]>([]);
    const dataFilters = useRef<ItemFilterType>({});
    const { minValue, maxValue, hasChanged, clearRange } =
        usePriceRangeContext();

    const removeItemFromFilter = (attribute: string) => {
        const index = actualFilters.current.indexOf(attribute);
        if (index >= 0) {
            actualFilters.current.splice(index, 1);
        }
    };

    const clearPriceRange = () => {
        clearRange();
        removeItemFromFilter(FILTERS.RANGE.ATTRIBUTE);
        window.dispatchEvent(new CustomEvent('clearPriceRange'));
    };

    const clearAll = () => {
        clearRange(() => {
            clearRefine();
            actualFilters.current = [];
            window.dispatchEvent(new CustomEvent('clearAllFilters'));
        });
    };

    useEffect(() => {
        dataFilters.current = transformFilterItems(items);
        const filters = transformFilters(items);
        const filtersDifference: string[] = filters.filter(
            item => !actualFilters.current.includes(item)
        );

        if (filtersDifference.length) {
            actualFilters.current = [
                ...actualFilters.current,
                ...filtersDifference.filter(
                    item => FILTERS.RANGE.ATTRIBUTE !== item
                ),
            ];
        } else {
            const [filterRemoved] = actualFilters.current
                .filter(item => FILTERS.RANGE.ATTRIBUTE !== item)
                .filter(item => !filters.includes(item));

            if (filterRemoved) {
                removeItemFromFilter(filterRemoved);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- only range deps
    }, [items]);

    useEffect(() => {
        const valuesHaveChanged =
            FILTERS.RANGE.MIN_VALUE !== minValue ||
            FILTERS.RANGE.MAX_VALUE !== maxValue;
        const includesPrice = actualFilters.current.includes(
            FILTERS.RANGE.ATTRIBUTE
        );

        if (valuesHaveChanged && !includesPrice) {
            actualFilters.current = [
                ...actualFilters.current,
                FILTERS.RANGE.ATTRIBUTE,
            ];
        }

        if (!valuesHaveChanged && includesPrice) {
            removeItemFromFilter(FILTERS.RANGE.ATTRIBUTE);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps -- only range deps
    }, [minValue, maxValue]);

    return (
        <div>
            {items.length ? (
                <div className="clear-filters" data-testid="clear-filters">
                    {hasHeader ? (
                        <div className="clear-filters__header">
                            <Heading
                                id="drawer-heading-1"
                                tagName="h4"
                                size="body-bold"
                            >
                                {FILTERS.CLEAR.HEADER}
                            </Heading>
                            <Button
                                variant="link"
                                size="sm"
                                text={FILTERS.CLEAR.ALL}
                                onClick={clearAll}
                                className="sparky-u-padding-right-sm"
                                data-testid="clear-all-filters-button"
                            />
                        </div>
                    ) : null}
                    <div className="clear-filters__content">
                        {actualFilters.current && (
                            <Chips>
                                {actualFilters.current
                                    ?.filter(
                                        (item, index) =>
                                            actualFilters.current.indexOf(
                                                item
                                            ) === index
                                    )
                                    .map(item => {
                                        if (
                                            item === FILTERS.RANGE.ATTRIBUTE &&
                                            hasChanged
                                        ) {
                                            return (
                                                <Chips.Item
                                                    data-testid={`clear-filter-${FILTERS.RANGE.ATTRIBUTE}`}
                                                    dismissible
                                                    text={getRangeTitle(
                                                        minValue,
                                                        maxValue,
                                                        true
                                                    )}
                                                    onClick={clearPriceRange}
                                                    key={item}
                                                />
                                            );
                                        }
                                        const filter =
                                            dataFilters?.current[item];
                                        return filter ? (
                                            <Chips.Item
                                                data-testid={`clear-filter-${item}-${filter?.value}`}
                                                dismissible
                                                text={filter.label}
                                                onClick={() =>
                                                    filter?.refine(
                                                        filter.refinement
                                                    )
                                                }
                                                key={item}
                                            />
                                        ) : null;
                                    })}
                                {!hasHeader ? (
                                    <li>
                                        <Button
                                            variant="link"
                                            text={FILTERS.CLEAR.ALL}
                                            onClick={clearAll}
                                            className="sparky-u-margin-right-sm sparky-u-margin-left-sm"
                                        />
                                    </li>
                                ) : null}
                            </Chips>
                        )}
                    </div>
                </div>
            ) : (
                ''
            )}
        </div>
    );
}
